/**
 * Every language the platform translates into — the full set our
 * transcription layer (ElevenLabs Scribe) itself supports, stored here as a
 * constant. Deliberately not fetched at runtime: the list changes on the
 * provider's schedule, not per request, and the product must know its own
 * languages without a network call. Source: elevenlabs.io/docs/capabilities/
 * speech-to-text (accuracy tiers), captured 2026-08-10.
 *
 * Codes are ISO 639-1 where one exists, 639-3 otherwise (yue, fil, ast,
 * nso). `label` is the English name the LLM is prompted with; `nativeLabel`
 * is what a speaker of the language would look for in a menu.
 *
 * Translation runs on the tenant's own LLM key — an LLM translates into any
 * of these; the tiers below describe *transcription* accuracy, which is why
 * they are not repeated here.
 */
export const TRANSLATION_LANGUAGES = [
  { code: "en", label: "English", nativeLabel: "English" },
  { code: "fr", label: "French", nativeLabel: "Français" },
  { code: "ar", label: "Arabic", nativeLabel: "العربية" },
  { code: "pt", label: "Portuguese", nativeLabel: "Português" },
  { code: "es", label: "Spanish", nativeLabel: "Español" },
  { code: "zh", label: "Mandarin Chinese", nativeLabel: "中文" },
  { code: "de", label: "German", nativeLabel: "Deutsch" },
  { code: "af", label: "Afrikaans", nativeLabel: "Afrikaans" },
  { code: "am", label: "Amharic", nativeLabel: "አማርኛ" },
  { code: "as", label: "Assamese", nativeLabel: "অসমীয়া" },
  { code: "ast", label: "Asturian", nativeLabel: "Asturianu" },
  { code: "az", label: "Azerbaijani", nativeLabel: "Azərbaycanca" },
  { code: "be", label: "Belarusian", nativeLabel: "Беларуская" },
  { code: "bg", label: "Bulgarian", nativeLabel: "Български" },
  { code: "bn", label: "Bengali", nativeLabel: "বাংলা" },
  { code: "bs", label: "Bosnian", nativeLabel: "Bosanski" },
  { code: "ca", label: "Catalan", nativeLabel: "Català" },
  { code: "cs", label: "Czech", nativeLabel: "Čeština" },
  { code: "cy", label: "Welsh", nativeLabel: "Cymraeg" },
  { code: "da", label: "Danish", nativeLabel: "Dansk" },
  { code: "el", label: "Greek", nativeLabel: "Ελληνικά" },
  { code: "et", label: "Estonian", nativeLabel: "Eesti" },
  { code: "fa", label: "Persian", nativeLabel: "فارسی" },
  { code: "fi", label: "Finnish", nativeLabel: "Suomi" },
  { code: "fil", label: "Filipino", nativeLabel: "Filipino" },
  { code: "ga", label: "Irish", nativeLabel: "Gaeilge" },
  { code: "gl", label: "Galician", nativeLabel: "Galego" },
  { code: "gu", label: "Gujarati", nativeLabel: "ગુજરાતી" },
  { code: "ha", label: "Hausa", nativeLabel: "Hausa" },
  { code: "he", label: "Hebrew", nativeLabel: "עברית" },
  { code: "hi", label: "Hindi", nativeLabel: "हिन्दी" },
  { code: "hr", label: "Croatian", nativeLabel: "Hrvatski" },
  { code: "hu", label: "Hungarian", nativeLabel: "Magyar" },
  { code: "hy", label: "Armenian", nativeLabel: "Հայերեն" },
  { code: "id", label: "Indonesian", nativeLabel: "Bahasa Indonesia" },
  { code: "ig", label: "Igbo", nativeLabel: "Igbo" },
  { code: "is", label: "Icelandic", nativeLabel: "Íslenska" },
  { code: "it", label: "Italian", nativeLabel: "Italiano" },
  { code: "ja", label: "Japanese", nativeLabel: "日本語" },
  { code: "jv", label: "Javanese", nativeLabel: "Basa Jawa" },
  { code: "ka", label: "Georgian", nativeLabel: "ქართული" },
  { code: "kk", label: "Kazakh", nativeLabel: "Қазақша" },
  { code: "km", label: "Khmer", nativeLabel: "ខ្មែរ" },
  { code: "kn", label: "Kannada", nativeLabel: "ಕನ್ನಡ" },
  { code: "ko", label: "Korean", nativeLabel: "한국어" },
  { code: "ku", label: "Kurdish", nativeLabel: "Kurdî" },
  { code: "ky", label: "Kyrgyz", nativeLabel: "Кыргызча" },
  { code: "lb", label: "Luxembourgish", nativeLabel: "Lëtzebuergesch" },
  { code: "lg", label: "Ganda", nativeLabel: "Luganda" },
  { code: "lo", label: "Lao", nativeLabel: "ລາວ" },
  { code: "lt", label: "Lithuanian", nativeLabel: "Lietuvių" },
  { code: "lv", label: "Latvian", nativeLabel: "Latviešu" },
  { code: "mi", label: "Māori", nativeLabel: "Te Reo Māori" },
  { code: "mk", label: "Macedonian", nativeLabel: "Македонски" },
  { code: "ml", label: "Malayalam", nativeLabel: "മലയാളം" },
  { code: "mn", label: "Mongolian", nativeLabel: "Монгол" },
  { code: "mr", label: "Marathi", nativeLabel: "मराठी" },
  { code: "ms", label: "Malay", nativeLabel: "Bahasa Melayu" },
  { code: "mt", label: "Maltese", nativeLabel: "Malti" },
  { code: "my", label: "Burmese", nativeLabel: "မြန်မာ" },
  { code: "ne", label: "Nepali", nativeLabel: "नेपाली" },
  { code: "nl", label: "Dutch", nativeLabel: "Nederlands" },
  { code: "no", label: "Norwegian", nativeLabel: "Norsk" },
  { code: "nso", label: "Northern Sotho", nativeLabel: "Sesotho sa Leboa" },
  { code: "oc", label: "Occitan", nativeLabel: "Occitan" },
  { code: "or", label: "Odia", nativeLabel: "ଓଡ଼ିଆ" },
  { code: "pa", label: "Punjabi", nativeLabel: "ਪੰਜਾਬੀ" },
  { code: "pl", label: "Polish", nativeLabel: "Polski" },
  { code: "ps", label: "Pashto", nativeLabel: "پښتو" },
  { code: "ro", label: "Romanian", nativeLabel: "Română" },
  { code: "ru", label: "Russian", nativeLabel: "Русский" },
  { code: "sd", label: "Sindhi", nativeLabel: "سنڌي" },
  { code: "sk", label: "Slovak", nativeLabel: "Slovenčina" },
  { code: "sl", label: "Slovenian", nativeLabel: "Slovenščina" },
  { code: "sn", label: "Shona", nativeLabel: "ChiShona" },
  { code: "so", label: "Somali", nativeLabel: "Soomaali" },
  { code: "sq", label: "Albanian", nativeLabel: "Shqip" },
  { code: "sr", label: "Serbian", nativeLabel: "Српски" },
  { code: "sv", label: "Swedish", nativeLabel: "Svenska" },
  { code: "sw", label: "Swahili", nativeLabel: "Kiswahili" },
  { code: "ta", label: "Tamil", nativeLabel: "தமிழ்" },
  { code: "te", label: "Telugu", nativeLabel: "తెలుగు" },
  { code: "tg", label: "Tajik", nativeLabel: "Тоҷикӣ" },
  { code: "th", label: "Thai", nativeLabel: "ไทย" },
  { code: "tr", label: "Turkish", nativeLabel: "Türkçe" },
  { code: "uk", label: "Ukrainian", nativeLabel: "Українська" },
  { code: "ur", label: "Urdu", nativeLabel: "اردو" },
  { code: "uz", label: "Uzbek", nativeLabel: "Oʻzbekcha" },
  { code: "vi", label: "Vietnamese", nativeLabel: "Tiếng Việt" },
  { code: "wo", label: "Wolof", nativeLabel: "Wolof" },
  { code: "xh", label: "Xhosa", nativeLabel: "isiXhosa" },
  { code: "yo", label: "Yoruba", nativeLabel: "Yorùbá" },
  { code: "yue", label: "Cantonese", nativeLabel: "廣東話" },
  { code: "zu", label: "Zulu", nativeLabel: "isiZulu" },
] as const;

export type TranslationLanguageCode = (typeof TRANSLATION_LANGUAGES)[number]["code"];

export const TRANSLATION_LANGUAGE_CODES = TRANSLATION_LANGUAGES.map(
  (l) => l.code,
) as [TranslationLanguageCode, ...TranslationLanguageCode[]];

export function languageLabel(code: string): string {
  return TRANSLATION_LANGUAGES.find((l) => l.code === code)?.label ?? code;
}

/**
 * Scripts written without spaces, where "break at whitespace" produces one
 * unbreakable run. Subtitle chunking falls back to character counts there,
 * with a narrower budget because these glyphs are roughly double-width.
 */
const SPACELESS = new Set(["zh", "yue", "ja", "th", "km", "lo", "my"]);

export function isSpaceless(code: string): boolean {
  return SPACELESS.has(code);
}
